import { useEffect, useState } from "react";

// @mui material components
import Card from "@mui/material/Card";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Grid from "@mui/material/Grid";
import Icon from "@mui/material/Icon";
import AddIcon from "@mui/icons-material/Add";

// RasPm Dashboard components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDSnackbar from "components/MDSnackbar";
import MDInput from "components/MDInput";

// RasPm Dashboard examples
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import DataTable from "examples/Tables/DataTable";

// Data
import dataTableData from "./data/dataTableData";
import { RequestService } from "services/RequestService";

// Components
import PositionForm from "../add";
import UpdatePosition from "../update";
import Cookies from "js-cookie";

function PositionsDataTable() {
  let userTypeName = Cookies.get("userTypeName");
  const CompanyAdmin = "CompanyAdmin".toUpperCase();
  const SuperAdmin = "SuperAdmin".toUpperCase();

  const isSuperAdmin = userTypeName?.toUpperCase() === SuperAdmin;

  const [tableData, setTableData] = useState(dataTableData);
  const [loading, setLoading] = useState(true);
  const [notification, setNotification] = useState({
    color: "info",
    icon: "notifications",
    title: "لوحة ادارة ربان السفينة",
    content: "",
    dateTime: new Date().toLocaleString('ar-IQ'),
    open: false,
  });
  const [updateDialog, setUpdateDialog] = useState({
    open: false,
    positionData: null
  });
  const [addDialog, setAddDialog] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState({
    open: false,
    positionId: null
  });

  const [positionForm, setPositionForm] = useState({
    name: "",
  });

  const [search, setSearch] = useState("");
  const [firstSearch, setFirstSearch] = useState(false);

  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 10,
    totalItems: 0
  });


  const closeNotification = () => setNotification({ ...notification, open: false });

  const showNotification = (message, type = "success", backendResponse = null) => {
    setNotification({
      color: type,
      icon: type === "success" ? "check" : "error",
      title: "لوحة ادارة ربان السفينة",
      content: backendResponse?.data?.message || message,
      dateTime: new Date().toLocaleString('ar-IQ'),
      open: true
    });
  };

  const handleUpdateClick = (positionData) => {
    const { index, ...positionToUpdate } = positionData;
    setUpdateDialog({ open: true, positionData: positionToUpdate });
  };

  const handleCloseUpdateDialog = () => {
    setUpdateDialog({ open: false, positionData: null });
  };

  const handleAddClick = () => {
    setPositionForm({
      name: "",
    });
    setAddDialog(true);
  };

  const handleCloseAddDialog = () => {
    setAddDialog(false);
    setPositionForm({
      name: "",
    });
  };

  const handleAddSubmit = async () => {
    if (!positionForm.name) {
      showNotification("الرجاء ادخال المسمى الوظيفي", "error");
    } else {
      try {
        setLoading(true);
        const response = await RequestService.post(`Positions/CreatePosition`, positionForm);

        if (response.data && !response.data.error) {
          showNotification("تم إضافة المسمى الوظيفي بنجاح", "success", response);
          handleCloseAddDialog();
          fetchPositions();
        } else {
          showNotification("حدث خطأ أثناء إضافة المسمى الوظيفي", "error", response);
        }
      } catch (error) {
        console.error("Error adding position:", error);
        showNotification(error.response?.data?.message || "حدث خطأ أثناء إضافة المسمى الوظيفي", "error", error.response);
      } finally {
        setLoading(false);
      }
    }
  };

  const handleOpenDeleteDialog = (positionId) => {
    setDeleteDialog({ open: true, positionId });
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialog({ open: false, positionId: null });
  };

  const handleDelete = async () => {
    try {
      setLoading(true);
      const response = await RequestService.delete(`Positions/${deleteDialog.positionId}`);
      if (response.data && !response.data.error) {
        showNotification("تم حذف المسمى الوظيفي بنجاح", "success", response);
        fetchPositions();
      } else {
        showNotification("حدث خطأ أثناء حذف المسمى الوظيفي", "error", response);
      }
    } catch (error) {
      console.error("Error deleting position:", error);
      showNotification(error.response?.data?.message || "حدث خطأ أثناء حذف المسمى الوظيفي", "error", error.response);
    } finally {
      setLoading(false);
      handleCloseDeleteDialog();
    }
  };

  const transformPositionData = (positions) => {
    return positions.map((position, index) => ({
      id: position.id,
      name: position.name || "—",
      actions: (
        isSuperAdmin ?
          <MDBox display="flex" alignItems="center" gap={1}>
            <MDButton
              variant="gradient"
              color="info"
              size="small"
              onClick={() => handleUpdateClick({ ...position, index: index + 1 })}
              startIcon={<Icon>edit</Icon>}
            >
              تعديل
            </MDButton>
            <MDButton
              variant="outlined"
              color="error"
              size="small"
              onClick={() => handleOpenDeleteDialog(position.id)}
              startIcon={<Icon>delete</Icon>}
            >
              حذف
            </MDButton>
          </MDBox>
          : ''
      ),
    }));
  };

  const fetchPositions = async () => {
    try {
      setLoading(true);
      const skip = (pagination.page - 1) * pagination.pageSize;
      const take = pagination.pageSize;

      const response = await RequestService.get(`Positions/get-positions?searchTerm=${search}&skip=${skip}&take=${take}`);
      if (response.data && !response.data.error) {
        setTableData({
          ...tableData,
          rows: transformPositionData(response.data.data)
        });
        setPagination({
          ...pagination,
          totalItems: response.data.totalCount
        });
      } else {
        showNotification("حدث خطأ أثناء جلب المسميات الوظيفية", "error", response);
      }
    } catch (error) {
      console.error("Error fetching positions:", error);
      showNotification(error.response?.data?.message || "حدث خطأ أثناء جلب المسميات الوظيفية", "error", error.response);
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (newPage) => {
    setPagination({ ...pagination, page: newPage });
  };

  const handlePageSizeChange = (newSize) => {
    setPagination({ page: 1, pageSize: newSize, totalItems: pagination.totalItems });
  };

  useEffect(() => {
    fetchPositions();
  }, [pagination.page, pagination.pageSize]);

  useEffect(() => {
    if (search || firstSearch) {
      setFirstSearch(true); {
        setPagination(prev => ({ ...prev, page: 1 }));
        const timer = setTimeout(() => {
          fetchPositions();
        }, 1000);
        return () => clearTimeout(timer);
      }
    }
  }, [search]);


  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                p={3}
                sx={{
                  backgroundColor: "grey.100",
                  borderBottom: "1px solid",
                  borderColor: "grey.300",
                }}
              >
                <MDBox>
                  <MDTypography variant="h6" fontWeight="medium">
                    قائمة المسميات الوظيفية
                  </MDTypography>
                  <MDTypography variant="button" color="text">
                    إدارة المسميات الوظيفية وبياناتها
                  </MDTypography>
                </MDBox>
                <MDBox width="14rem" ml="auto">
                  <MDInput
                    placeholder="بحث ..."
                    value={search}
                    fullWidth
                    onChange={({ currentTarget }) => {
                      setSearch(currentTarget.value);
                    }}
                    sx={{ margin: "0 -10px" }}
                  />
                </MDBox>
                <MDButton
                  variant="gradient"
                  color="info"
                  startIcon={<AddIcon />}
                  onClick={handleAddClick}
                >
                  إضافة مسمى وظيفي
                </MDButton>
              </MDBox>
              <MDBox p={3}>
                <DataTable
                  table={tableData}
                  isSorted={true}
                  entriesPerPage={{
                    defaultValue: pagination.pageSize,
                    entries: [5, 10, 15, 20, 25],
                    onChange: handlePageSizeChange
                  }}
                  showTotalEntries={true}
                  noEndBorder
                  loading={loading}
                  pagination={{
                    variant: "gradient",
                    color: "info",
                    page: pagination.page,
                    totalPages: Math.ceil(pagination.totalItems / pagination.pageSize),
                    totalItems: pagination.totalItems,
                    onPageChange: handlePageChange
                  }}
                />
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>

      {/* باقي الكود بدون تغيير */}
      {/* Add Position Dialog */}
      <Dialog
        open={addDialog}
        onClose={handleCloseAddDialog}
        maxWidth="sm"
        fullWidth
        dir="rtl"
      >
        <DialogTitle>إضافة مسمى وظيفي جديد</DialogTitle>
        <DialogContent>
          <PositionForm
            formData={positionForm}
            handleChange={setPositionForm}
          />
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'space-between', p: 2, gap: 1 }}>
          <MDButton onClick={handleAddSubmit} variant="gradient" color="info" size="small">
            إضافة
          </MDButton>
          <MDButton onClick={handleCloseAddDialog} variant="outlined" color="error" size="small">
            إلغاء
          </MDButton>
        </DialogActions>
      </Dialog>

      {/* Update Position Dialog */}
      <UpdatePosition
        open={updateDialog.open}
        onClose={handleCloseUpdateDialog}
        position={updateDialog.positionData}
        onSuccess={(message, type) => {
          showNotification(message, type);
          fetchPositions();
        }}
      />

      {/* Delete Position Dialog */}
      <Dialog
        open={deleteDialog.open}
        onClose={handleCloseDeleteDialog}
        maxWidth="sm"
        fullWidth
        dir="rtl"
      >
        <DialogTitle>
          <MDTypography variant="h6">تأكيد الحذف</MDTypography>
        </DialogTitle>
        <DialogContent>
          <MDTypography variant="body1">
            هل أنت متأكد من حذف هذا المسمى الوظيفي؟
          </MDTypography>
        </DialogContent>

        <DialogActions sx={{ justifyContent: 'space-between', p: 2, gap: 1 }}>
          <MDButton onClick={handleDelete} color="error" size="small">
            حذف
          </MDButton>
          <MDButton onClick={handleCloseDeleteDialog} color="secondary" size="small">
            إلغاء
          </MDButton>
        </DialogActions>
      </Dialog>

      <MDSnackbar
        color={notification.color}
        icon={notification.icon}
        title={notification.title}
        content={notification.content}
        dateTime={notification.dateTime}
        open={notification.open}
        onClose={closeNotification}
        close={closeNotification}
        bgWhite
        dir="rtl"
      />

      <Footer />
    </DashboardLayout>
  );
}

export default PositionsDataTable;